import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOwnerView } from '../context/OwnerViewContext';
import { useDesktopTopBar } from '../context/DesktopLayoutContext';
import { getSalesOverviewByDays, getSnapshotSummary, todayStr, yesterdayStr, getStartEndFromFilter } from '../services/reportsApi';
import { getSnapshotDetails, getSnapshotComparison } from '../redux/reports/clickHouse/clickHouseApi';
import DesktopLayout from './layout/DesktopLayout';
import Phone from './Phone';
import BottomNav from './BottomNav';
import TopBar from './layout/TopBar';
import { T, useIsDesktop, Bone } from './reports/reportTokens';
import SectionEmpty from './reports/SectionEmpty';
import SectionError from './reports/SectionError';
import { ReportSectionExportButton } from './reports/components/ReportExportButton';
import { getCurrencySymbol, useCurrencySymbol, formatMoneyForExport } from '../utils/currency';
import { comparisonTooltip, exportPeriodFor } from '../utils/reportPeriod';
import { usePullToRefresh } from '../utils/pullToRefresh/usePullToRefresh';
import { PullToRefreshIndicator } from '../utils/pullToRefresh/PullToRefreshIndicator';

// ─── Constants ─────────────────────────────────────────────
const ROW_LIMITS = [10, 20, 31, 50];

// Global sales-channel filter — same three options / pill control as Snapshot & Sales
// (reports-channel-filter skill), rendered via TopBar's `sourceFilters` next to the date
// filter. Drives both the stamp KPI row and the Sales Overview table's columns.
const CHANNEL_OPTIONS = ['Overall sales', 'Direct sales', '3rd party sales'];

const OVERALL_COLS = [
    { key: 'date', label: 'Date', sortable: true },
    { key: 'totalGrossSalesIncludingThirdparty', label: 'Total Collected', sortable: true, isCurrency: true },
    { key: 'totalNetSalesIncludingThirdparty', label: 'Net Sales', sortable: true, isCurrency: true },
    { key: 'totalTaxIncludingThirdparty', label: 'Tax', sortable: true, isCurrency: true },
    { key: 'tips', label: 'Tips', sortable: true, isCurrency: true },
    { key: 'gratuity', label: 'Gratuity', sortable: true, isCurrency: true },
    { key: 'totalOrdersIncludingThirdparty', label: 'Orders', sortable: true, isInt: true, align: 'center' },
    { key: 'discounts', label: 'Discounts', sortable: true, isCurrency: true },
    { key: 'totalRefundsIncludingThirdparty', label: 'Refund', sortable: true, isCurrency: true, align: 'center' },
];

const DIRECT_COLS = [
    { key: 'date', label: 'Date', sortable: true },
    { key: 'magilSales', label: 'Total Collected', sortable: true, isCurrency: true },
    { key: 'magilNetSales', label: 'Net Sales', sortable: true, isCurrency: true },
    { key: 'magilTax', label: 'Tax', sortable: true, isCurrency: true },
    { key: 'magilTips', label: 'Tips', sortable: true, isCurrency: true },
    { key: 'magilGratuity', label: 'Gratuity', sortable: true, isCurrency: true },
    { key: 'magilOrders', label: 'Orders', sortable: true, isInt: true, align: 'center' },
    { key: 'magilDiscounts', label: 'Discounts', sortable: true, isCurrency: true },
    { key: 'magilRefunds', label: 'Refund', sortable: true, isCurrency: true, align: 'center' },
];

const THIRD_PARTY_COLS = [
    { key: 'date', label: 'Date', sortable: true },
    { key: 'thirdPartySales', label: 'Total Collected', sortable: true, isCurrency: true },
    { key: 'thirdPartyNetSales', label: 'Net Sales', sortable: true, isCurrency: true },
    { key: 'thirdPartyTax', label: 'Tax', sortable: true, isCurrency: true },
    { key: 'thirdPartyTips', label: 'Tips', sortable: true, isCurrency: true },
    { key: 'thirdPartyGratuity', label: 'Gratuity', sortable: true, isCurrency: true },
    { key: 'thirdPartyOrders', label: 'Orders', sortable: true, isInt: true, align: 'center' },
    { key: 'thirdPartyDiscounts', label: 'Discounts', sortable: true, isCurrency: true },
    { key: 'thirdPartyRefunds', label: 'Refund', sortable: true, isCurrency: true, align: 'center' },
];

const getColsForView = (channel: string) => {
    if (channel === 'Direct sales') return DIRECT_COLS;
    if (channel === '3rd party sales') return THIRD_PARTY_COLS;
    return OVERALL_COLS;
};

// Title-cases a CHANNEL_OPTIONS value for display, e.g. '3rd party sales' -> '3rd Party Sales'.
const formatChannelLabel = (channel: string) =>
    channel.replace(/\b\w/g, (c) => c.toUpperCase());

// ─── Utils ─────────────────────────────────────────────────
const num = (v: any) => Number(v) || 0;

const numberFmt = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const intFmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

const fmtC = (v: any) => `${getCurrencySymbol()}${numberFmt.format(num(v))}`;
const fmtI = (v: any) => intFmt.format(num(v));

const fmtCol = (col: any, value: any) => {
    if (col.isCurrency) return fmtC(value);
    if (col.isInt) return fmtI(value);
    return value ?? '—';
};

/**
 * Stamp-KPI figures for the selected channel, read from the same `getSalesOverviewByDays`
 * `totals` object the Sales Overview table uses — Direct/3rd party have no BE-combined
 * tips+gratuity field (unlike Overall's `totalTip`), so it's summed here from the two.
 */
const getKpiTotals = (totals: any, channel: string) => {
    if (channel === 'Direct sales') {
        return {
            netSales: totals?.magilNetSales, totalCollected: totals?.magilSales,
            orders: totals?.magilOrders, tax: totals?.magilTax,
            tipsAndGratuity: num(totals?.magilTips) + num(totals?.magilGratuity),
            discounts: totals?.magilDiscounts,
        };
    }
    if (channel === '3rd party sales') {
        return {
            netSales: totals?.thirdPartyNetSales, totalCollected: totals?.thirdPartySales,
            orders: totals?.thirdPartyOrders, tax: totals?.thirdPartyTax,
            tipsAndGratuity: num(totals?.thirdPartyTips) + num(totals?.thirdPartyGratuity),
            discounts: totals?.thirdPartyDiscounts,
        };
    }
    return {
        netSales: totals?.totalNetSalesIncludingThirdparty, totalCollected: totals?.totalGrossSalesIncludingThirdparty,
        orders: totals?.totalOrdersIncludingThirdparty, tax: totals?.totalTaxIncludingThirdparty,
        tipsAndGratuity: totals?.totalTip, discounts: totals?.discounts,
    };
};



// Delegates to the shared resolver (utils/reportPeriod.resolveTokenRange via reportsApi's
// getStartEndFromFilter) instead of recomputing ranges locally — this is the same resolver
// SnapshotScreen/SalesScreen use, so e.g. '7D' anchors on the store's 5 AM cutoff (yesterday)
// exactly like every other report, rather than a naive "today minus 6" window. A local copy
// here previously drifted from Snapshot's dates by two days for the same '7D' selection.
const getDateRange = (filter: string, today: string, yesterday: string) => {
    const range = getStartEndFromFilter(filter, today, yesterday);
    return range ?? { startDate: today, endDate: today };
};

const getTableData = (resp: any) =>
    resp?.data || resp?.content || (Array.isArray(resp) ? resp : []);

const parseApiResponse = (res: any) => {
    let resp = res;
    if (res?.metaDataInfo?.data && typeof res.metaDataInfo.data === 'object' && 'totalPages' in res.metaDataInfo.data) {
        resp = res.metaDataInfo.data;
    }
    if (Array.isArray(resp)) {
        resp = { data: resp, totalPages: 1, totalElements: resp.length, totals: null };
    }
    return resp;
};

// ─── Styles ────────────────────────────────────────────────
const S: Record<string, any> = {
    flex: (gap = 16) => ({ display: 'flex', gap }),
    flexWrap: (gap = 16) => ({ display: 'flex', gap, flexWrap: 'wrap' as const }),
    card: { background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 12, overflow: 'hidden' },
    thBase: {
        fontWeight: 600, borderBottom: `1px solid ${T.divider}`, whiteSpace: 'nowrap' as const,
        background: '#F9F9FB', textTransform: 'uppercase' as const, letterSpacing: 0.3, userSelect: 'none' as const,
    },
    tdBase: { color: '#262626', whiteSpace: 'nowrap' as const, borderBottom: `1px solid ${T.divider}` },
    accentGradient: 'linear-gradient(135deg, #E52333 0%, #C41E2E 100%)',
    accentShadow: '0 4px 16px rgba(229,35,51,0.18)',
};

// ─── Custom Hooks ──────────────────────────────────────────

/** Shared dashboard state for Desktop & Mobile */
const useOwnerDashboard = ({ isDesktop }: { isDesktop: boolean }) => {
    const { auth } = useAuth();
    const ownerView = useOwnerView();
    const { isSuperUser, locationsLoading } = ownerView;
    const setTopBarProps = useDesktopTopBar();

    const today = useMemo(() => todayStr(), []);
    const yesterday = useMemo(() => yesterdayStr(), []);
    const [activeDate, setActiveDate] = useState('Today');
    const [channel, setChannel] = useState('Overall sales');
    const [sortBy, setSortBy] = useState('');
    const [sortDir, setSortDir] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleDateChange = useCallback((val: string) => setActiveDate(val), []);

    // Push to TopBar — channel filter rendered via `sourceFilters`, alongside the date
    // filter, same mechanism as Snapshot/Sales (reports-channel-filter skill).
    useEffect(() => {
        const sourceFilters = { options: CHANNEL_OPTIONS, value: channel, onChange: setChannel };
        setTopBarProps(isDesktop
            ? { breadcrumbs: 'Home', title: 'Owner Dashboard', dateFilters: { value: activeDate, onChange: setActiveDate }, sourceFilters }
            : { title: 'Owner Dashboard', showBack: false, hideOutlet: true, dateFilters: { value: activeDate, onChange: handleDateChange }, sourceFilters }
        );
    }, [setTopBarProps, activeDate, handleDateChange, isDesktop, channel]);

    const dates = useMemo(() => getDateRange(activeDate, today, yesterday), [activeDate, today, yesterday]);
    const locationId = useMemo(() => ownerView.selectedStore?.id ?? ownerView.selectedStore?.locationId ?? '', [ownerView.selectedStore]);
    const isAllStores = ownerView.selectedStore?.isAll === true;

    const handleSort = useCallback((key: string) => {
        setSortDir((prev) =>
            sortBy !== key ? 'asc' :
            prev === 'asc' ? 'desc' :
            prev === 'desc' ? null : 'asc'
        );
        if (sortBy !== key) setSortBy(key);
        else if (sortDir === 'desc') setSortBy('');
    }, [sortBy, sortDir]);

    const columns = useMemo(() => getColsForView(channel), [channel]);

    // Reset page on filter change
    useEffect(() => { setCurrentPage(1); }, [locationId, dates, sortBy, sortDir, rowsPerPage, channel]);

    return {
        ...ownerView, auth, today, yesterday,
        activeDate, setActiveDate,
        channel, setChannel,
        sortBy, sortDir, handleSort,
        currentPage, setCurrentPage,
        rowsPerPage, setRowsPerPage,
        dates, locationId, isAllStores, columns,
    };
};

/** Centralized API fetch logic */
const useSalesData = ({ locationId, dates, activeDate, today, sortBy, sortDir, currentPage, rowsPerPage, isAllStores, auth }: any) => {
    const [tableResp, setTableResp] = useState<any>(null);
    const [tableLoading, setTableLoading] = useState(false);
    const [tableError, setTableError] = useState(false);
    const [totals, setTotals] = useState<any>(null);
    const [retryCount, setRetryCount] = useState(0);

    const token = auth?.accessToken ?? auth?.access_token ?? null;
    const retry = useCallback(() => setRetryCount((n) => n + 1), []);

    useEffect(() => {
        if (!locationId || !dates.startDate) return;
        setTableLoading(true);
        setTableError(false);
        // Compare resolved dates, not the activeDate token — stepping the date arrows
        // back then forward onto today resolves to a 'custom:from:to' token even though
        // the range IS today (stepPeriod never re-collapses to the literal 'Today').
        const isToday = dates.startDate === today && dates.endDate === today;
        const payload: any = {
            locationId,
            page: currentPage - 1,
            size: rowsPerPage,
        };
        if (!isToday) {
            payload.startDate = dates.startDate;
            payload.endDate = dates.endDate;
        }
        if (sortBy) { payload.sortBy = sortBy; payload.sortAsc = sortDir === 'asc'; }
        if (isAllStores) payload.considerThirdPartyValues = true;

        getSalesOverviewByDays(payload)
            .then((res) => {
                const resp = parseApiResponse(res);
                setTableResp(resp);
                setTableError(false);
                if (resp?.totals) setTotals(resp.totals);
            })
            .catch(() => { setTableResp(null); setTableError(true); })
            .finally(() => setTableLoading(false));
    }, [locationId, dates, activeDate, today, sortBy, sortDir, currentPage, rowsPerPage, isAllStores, token, retryCount]);

    const tableData = useMemo(() => getTableData(tableResp), [tableResp]);

    return { tableResp, tableLoading, tableError, retry, totals, tableData };
};

// ─── Small Components ──────────────────────────────────────

const Select = ({ label, options, value, onChange, disabled, style }: any) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    const activeIdx = options.findIndex((o) => {
        if (o.value !== undefined) {
            return typeof o.value === 'object'
                ? JSON.stringify(o.value) === JSON.stringify(value)
                : o.value === value;
        }
        return o === value;
    });
    const activeOpt = activeIdx >= 0 ? options[activeIdx] : null;
    const displayLabel = activeOpt?.label ?? activeOpt ?? '';

    useEffect(() => {
        if (!open) return;
        const onDown = (e: any) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e: any) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
    }, [open]);

    return (
        <div ref={ref} style={{ position: 'relative', ...style }}>
            {label && <div style={{ fontSize: 11, fontWeight: 600, color: '#9CA3B8', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>}
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setOpen((p) => !p)}
                style={{
                    width: '100%', height: 42, borderRadius: 10,
                    border: open ? '1.5px solid #E52333' : '1px solid #E0E4ED',
                    background: disabled ? '#F7F8FA' : '#FFFFFF',
                    padding: '0 36px 0 14px', fontSize: 13, fontWeight: 500, color: '#1A1D2E',
                    cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: T.font,
                    outline: 'none', textAlign: 'left',
                    display: 'flex', alignItems: 'center',
                    boxShadow: open ? '0 0 0 3px rgba(229,35,51,0.08)' : '0 1px 2px rgba(0,0,0,0.04)',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                    position: 'relative', overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
            >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{displayLabel}</span>
                <span style={{
                    position: 'absolute', right: 10, top: '50%', transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`,
                    transition: 'transform 0.2s', display: 'flex', alignItems: 'center', color: '#9CA3B8', pointerEvents: 'none',
                }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </span>
            </button>
            {open && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 100,
                    background: '#FFFFFF', border: '1px solid #E0E4ED', borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.04)',
                    maxHeight: 220, overflowY: 'auto', padding: '4px 0',
                }}>
                    {options.map((opt, i) => {
                        const isActive = i === activeIdx;
                        return (
                            <div
                                key={i}
                                onClick={() => { onChange(opt.value !== undefined ? opt.value : opt); setOpen(false); }}
                                style={{
                                    padding: '9px 14px', fontSize: 13, fontWeight: isActive ? 600 : 400,
                                    color: isActive ? '#E52333' : '#1A1D2E',
                                    background: isActive ? '#FEF2F2' : 'transparent',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                                    transition: 'background 0.1s',
                                }}
                                onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => { if (!isActive) e.currentTarget.style.background = '#F7F8FA'; }}
                                onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => { e.currentTarget.style.background = isActive ? '#FEF2F2' : 'transparent'; }}
                            >
                                {isActive && (
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                        <path d="M3.5 7L6 9.5L10.5 4.5" stroke="#E52333" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                )}
                                <span style={{ marginLeft: isActive ? 0 : 22 }}>{opt.label ?? opt}</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const KPICard = ({ label, value, icon, accent }: any) => (
    <div style={{
        flex: 1, minWidth: 155,
        background: accent ? S.accentGradient : '#FFFFFF',
        border: accent ? 'none' : '1px solid #E8EAF0',
        borderRadius: 14, padding: '20px 22px',
        boxShadow: accent ? S.accentShadow : '0 1px 4px rgba(0,0,0,0.04)',
        position: 'relative', overflow: 'hidden',
    }}>
        {accent && <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            {icon && <span className="material-symbols-rounded" style={{ fontSize: 16, color: accent ? 'rgba(255,255,255,0.7)' : '#9CA3B8' }}>{icon}</span>}
            <span style={{ fontSize: 11, fontWeight: 600, color: accent ? 'rgba(255,255,255,0.75)' : '#9CA3B8', textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</span>
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: accent ? '#FFFFFF' : '#1A1D2E', letterSpacing: -0.5 }}>{value}</div>
    </div>
);

const Filters = ({ brands, selectedBrand, changeBrand, filteredStores, selectedStore, changeStore, isSuperUser, styleOverride }: any) => (
    <div style={{ ...S.flexWrap(12), ...styleOverride }}>
        {brands.length > 2 && (
            <Select label="Select Brand" options={brands.map((b) => ({ label: b.label, value: b }))} value={selectedBrand}
                onChange={changeBrand} disabled={brands.length <= 2}
                style={styleOverride?.brandStyle ?? { minWidth: 180 }}
            />
        )}
        <Select label="Select Store" options={filteredStores.map((s) => ({
            label: s.locationName ?? s.name ?? 'Store', value: s,
        }))} value={selectedStore}
            onChange={changeStore}
            disabled={filteredStores.length <= 1 || !isSuperUser}
            style={styleOverride?.storeStyle ?? { minWidth: 260 }}
        />
    </div>
);

const KPIRow = ({ totals, loading, variant, channel }: any) => {
    const t = getKpiTotals(totals, channel);
    const kpis = [
        { label: 'Total Collected', value: fmtC(t.totalCollected ?? 0), icon: 'trending_up' },
        { label: 'Orders', value: fmtI(t.orders ?? 0), icon: 'receipt_long' },
        { label: 'Tax', value: fmtC(t.tax ?? 0), icon: 'account_balance' },
        { label: 'Tips and Gratuity', value: fmtC(t.tipsAndGratuity ?? 0), icon: 'volunteer_activism' },
        { label: 'Discounts', value: fmtC(t.discounts ?? 0), icon: 'sell' },
    ];
    const netSales = fmtC(t.netSales ?? 0);

    if (variant === 'mobile') {
        return (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16, opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                <div style={{
                    gridColumn: '1 / -1', background: S.accentGradient, borderRadius: 14, padding: '20px 22px',
                    boxShadow: S.accentShadow,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                        <span className="material-symbols-rounded" style={{ fontSize: 16, color: 'rgba(255,255,255,0.7)' }}>payments</span>
                        <span style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Net Sales</span>
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#FFFFFF' }}>{netSales}</div>
                </div>
                {kpis.map((k) => (
                    <div key={k.label} style={{ background: '#F8F9FC', borderRadius: 10, padding: '14px 16px', border: '1px solid #E0E0E0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                            <span className="material-symbols-rounded" style={{ fontSize: 14, color: '#9CA3B8' }}>{k.icon}</span>
                            <span style={{ fontSize: 10, fontWeight: 600, color: '#6F6F6F', textTransform: 'uppercase', letterSpacing: 0.5 }}>{k.label}</span>
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: '#262626' }}>{k.value}</div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div style={{ ...S.flex(14), marginBottom: 24, flexWrap: 'wrap', opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
            <KPICard icon="payments" label="Net Sales" value={netSales} accent />
            <KPICard icon="trending_up" label="Total Collected" value={kpis[0].value} />
            <KPICard icon="receipt_long" label="Orders" value={kpis[1].value} />
            <KPICard icon="account_balance" label="Tax" value={kpis[2].value} />
            <KPICard icon="volunteer_activism" label="Tips and Gratuity" value={kpis[3].value} />
            <KPICard icon="sell" label="Discounts" value={kpis[4].value} />
        </div>
    );
};

const Pagination = ({ currentPage, totalPages, totalElements, rowsPerPage, onPageChange, onRowsPerPageChange }: any) => {
    const startItem = (currentPage - 1) * rowsPerPage + 1;
    const endItem = Math.min(currentPage * rowsPerPage, totalElements || 0);

    return (
        <div style={{
            display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center',
            padding: '12px 16px', borderTop: `1px solid ${T.divider}`, fontSize: 12, color: T.t2, gap: 12,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>Rows per page:</span>
                <select
                    value={rowsPerPage}
                    onChange={(e) => { onRowsPerPageChange(Number(e.target.value)); onPageChange(1); }}
                    style={{ border: `1px solid ${T.cardBorder}`, borderRadius: 4, padding: '2px 6px', fontSize: 12, fontFamily: T.font, background: T.surface, outline: 'none' }}
                >
                    {ROW_LIMITS.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {(() => {
                    const isPrevDisabled = currentPage <= 1;
                    const isNextDisabled = currentPage >= totalPages;
                    const btnStyle = (disabled: boolean) => ({
                        height: 30, padding: '0 14px', borderRadius: 6, border: `1px solid ${T.cardBorder}`,
                        background: disabled ? T.hover : T.surface,
                        cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 12,
                        color: disabled ? T.t3 : T.t2, fontFamily: T.font, fontWeight: 500,
                        display: 'flex', alignItems: 'center', gap: 6,
                    });
                    return (<>
                        <button type="button" disabled={isPrevDisabled} onClick={() => onPageChange(currentPage - 1)} style={btnStyle(isPrevDisabled)}>
                            <span>&#8249;</span>
                        </button>
                        <span style={{ fontSize: 12, color: T.t2, whiteSpace: 'nowrap' }}>
                            {totalElements > 0 ? `${startItem}–${endItem} of ${totalElements}` : 'No records'}
                        </span>
                        <button type="button" disabled={isNextDisabled} onClick={() => onPageChange(currentPage + 1)} style={btnStyle(isNextDisabled)}>
                            <span>&#8250;</span>
                        </button>
                    </>);
                })()}
            </div>
            <div />
        </div>
    );
};

const SalesTable = ({ columns, data, totals, loading, tableError, onRetry, sortBy, sortDir, onSort, currentPage, totalPages, totalElements, rowsPerPage, onPageChange, onRowsPerPageChange, buildExport, exportDateRangeLabel, channel }: any) => (
    <div style={S.card}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#262626' }}>Sales Overview</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {!!totalElements && (
                    <ReportSectionExportButton
                        title={`Sales Overview (${formatChannelLabel(channel ?? 'Overall sales')})`}
                        filename="owner-dashboard-sales-overview"
                        dateRangeLabel={exportDateRangeLabel}
                        metaFields={[['Channel', formatChannelLabel(channel ?? 'Overall sales')]]}
                        buildExport={buildExport}
                        variant="desktop"
                    />
                )}
            </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, tableLayout: 'fixed', minWidth: 900 }}>
                <colgroup>
                    <col style={{ width: '12%' }} />
                    {columns.slice(1).map((col) => <col key={col.key} style={{ width: `${88 / (columns.length - 1)}%` }} />)}
                </colgroup>
                <thead>
                    <tr>
                        {columns.map((col, ci) => (
                            <th key={col.key} onClick={col.sortable ? () => onSort(col.key) : undefined}
                                style={{ ...S.thBase, padding: '11px 14px', paddingLeft: ci === 2 ? 28 : 14, textAlign: col.align ?? 'left', color: sortBy === col.key ? '#E52333' : '#6F6F6F', cursor: col.sortable ? 'pointer' : 'default', fontSize: 11.5 }}>
                                {col.label}
                                {sortBy === col.key && <span style={{ marginLeft: 3 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        Array.from({ length: rowsPerPage }).map((_, i) => (
                            <tr key={i}>{columns.map((col) => <td key={col.key} style={{ padding: '11px 14px' }}><Bone w={col.key === 'date' ? 80 : 60} h={14} /></td>)}</tr>
                        ))
                    ) : tableError ? (
                        <tr><td colSpan={columns.length}><SectionError kpiName="sales overview" onRetry={onRetry} size="medium" minHeight={240} /></td></tr>
                    ) : !data || data.length === 0 ? (
                        <tr><td colSpan={columns.length}><SectionEmpty kpiName="sales overview" size="medium" minHeight={220} /></td></tr>
                    ) : (
                        <>
                            {data.map((row, i) => (
                                <tr key={i} style={{ background: i % 2 === 0 ? '#FFFFFF' : '#FAFBFC' }}>
                                    {columns.map((col, ci) => (
                                        <td key={col.key} style={{ ...S.tdBase, padding: '11px 14px', paddingLeft: ci === 2 ? 28 : 14, textAlign: col.align ?? 'left', fontWeight: col.key.includes('Net') ? 600 : 400 }}>
                                            {fmtCol(col, row[col.key])}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                            {totals && (
                                <tr style={{ background: '#F5F5F5' }}>
                                    {columns.map((col, ci) => (
                                        <td key={col.key} style={{ ...S.tdBase, padding: '11px 14px', paddingLeft: ci === 2 ? 28 : 14, textAlign: col.align ?? 'left', fontWeight: 700, color: '#1A1D2E' }}>
                                            {ci === 0 ? 'TOTALS' : fmtCol(col, totals[col.key])}
                                        </td>
                                    ))}
                                </tr>
                            )}
                        </>
                    )}
                </tbody>
            </table>
        </div>
        <Pagination currentPage={currentPage} totalPages={totalPages || 1} totalElements={totalElements || 0}
            rowsPerPage={rowsPerPage} onPageChange={onPageChange} onRowsPerPageChange={onRowsPerPageChange} />
    </div>
);

// ─── Shimmer Skeletons ─────────────────────────────────────
const shimmerCSS = `@keyframes shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }`;
let shimmerInjected = false;
const injectShimmer = () => { if (shimmerInjected) return; shimmerInjected = true; const s = document.createElement('style'); s.textContent = shimmerCSS; document.head.appendChild(s); };

const ShimmerBox = ({ w, h, r = 8, style: extra }: any) => {
    injectShimmer();
    return (
        <div style={{
            width: w ?? '100%', height: h, borderRadius: r,
            background: 'linear-gradient(90deg, #E8EAF0 25%, #F3F4F8 37%, #E8EAF0 63%)',
            backgroundSize: '800px 100%', animation: 'shimmer 1.4s ease infinite',
            ...extra,
        }} />
    );
};

const DesktopOwnerShimmer = () => (
    <div style={{ padding: 24, maxWidth: 1280 }}>
        <div style={S.flex(16)}>
            <div style={{ minWidth: 180 }}><ShimmerBox w={80} h={12} r={4} style={{ marginBottom: 8 }} /><ShimmerBox w={180} h={42} r={10} /></div>
            <div style={{ minWidth: 260 }}><ShimmerBox w={80} h={12} r={4} style={{ marginBottom: 8 }} /><ShimmerBox w={260} h={42} r={10} /></div>
        </div>
        <div style={{ ...S.flexWrap(14), marginTop: 20, marginBottom: 24 }}>
            {[true, false, false, false, false, false].map((accent, i) => (
                <div key={i} style={{
                    flex: 1, minWidth: 155, borderRadius: 14, padding: '20px 22px',
                    background: accent ? S.accentGradient : '#FFFFFF',
                    border: accent ? 'none' : '1px solid #E8EAF0',
                    boxShadow: accent ? S.accentShadow : '0 1px 4px rgba(0,0,0,0.04)',
                }}>
                    <ShimmerBox w={70} h={11} r={4} style={{ marginBottom: 12, opacity: accent ? 0.4 : 1 }} />
                    <ShimmerBox w={120} h={26} r={6} style={{ opacity: accent ? 0.3 : 1 }} />
                </div>
            ))}
        </div>
        <div style={{ background: '#FFFFFF', border: '1px solid #E8EAF0', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #F0F0F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <ShimmerBox w={140} h={18} r={6} />
                <div style={S.flex(12)}><ShimmerBox w={70} h={28} r={14} /><ShimmerBox w={55} h={28} r={14} /><ShimmerBox w={65} h={28} r={14} /></div>
            </div>
            <div style={{ display: 'flex', padding: '12px 14px', background: '#F9F9FB', borderBottom: '1px solid #F0F0F0' }}>
                {Array.from({ length: 9 }).map((_, i) => (
                    <div key={i} style={{ flex: i === 0 ? '0 0 12%' : 1, padding: '0 4px' }}><ShimmerBox w={i === 0 ? 50 : 65} h={12} r={4} /></div>
                ))}
            </div>
            {Array.from({ length: 10 }).map((_, ri) => (
                <div key={ri} style={{ display: 'flex', padding: '12px 14px', borderBottom: '1px solid #F6F6F8', background: ri % 2 === 0 ? '#FFFFFF' : '#FAFBFC' }}>
                    {Array.from({ length: 9 }).map((_, ci) => (
                        <div key={ci} style={{ flex: ci === 0 ? '0 0 12%' : 1, padding: '0 4px' }}><ShimmerBox w={ci === 0 ? 80 : 55 + (ci * 7 % 20)} h={14} r={4} /></div>
                    ))}
                </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #F0F0F0' }}>
                <ShimmerBox w={100} h={14} r={4} /><ShimmerBox w={80} h={14} r={4} />
                <div style={S.flex(6)}><ShimmerBox w={30} h={30} r={6} /><ShimmerBox w={30} h={30} r={6} /></div>
            </div>
        </div>
    </div>
);

const MobileOwnerShimmer = () => (
    <div style={{ padding: '16px 20px' }}>
        <ShimmerBox w={80} h={11} r={4} style={{ marginBottom: 6 }} />
        <ShimmerBox h={42} r={10} style={{ marginBottom: 12 }} />
        <ShimmerBox w={80} h={11} r={4} style={{ marginBottom: 6 }} />
        <ShimmerBox h={42} r={10} style={{ marginBottom: 16 }} />
        <div style={{ width: '100%', background: S.accentGradient, borderRadius: 14, padding: '20px 22px', marginBottom: 10, boxShadow: S.accentShadow }}>
            <ShimmerBox w={60} h={10} r={4} style={{ opacity: 0.3, marginBottom: 10 }} />
            <ShimmerBox w={160} h={28} r={6} style={{ opacity: 0.25 }} />
        </div>
        <div style={S.flexWrap(10)}>
            {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} style={{ flex: 1, minWidth: '45%', background: '#F8F9FC', borderRadius: 10, padding: '14px 16px', border: '1px solid #E0E0E0' }}>
                    <ShimmerBox w={60} h={10} r={4} style={{ marginBottom: 8 }} /><ShimmerBox w={90} h={18} r={4} />
                </div>
            ))}
        </div>
        <div style={{ ...S.flex(12), margin: '16px 0 12px' }}><ShimmerBox w={70} h={28} r={6} /><ShimmerBox w={55} h={28} r={6} /><ShimmerBox w={65} h={28} r={6} /></div>
        <div style={{ background: '#FFFFFF', borderRadius: 10, border: '1px solid #E0E0E0', overflow: 'hidden' }}>
            {Array.from({ length: 6 }).map((_, ri) => (
                <div key={ri} style={{ ...S.flex(8), padding: '10px 12px', borderBottom: '1px solid #F0F0F0' }}>
                    {Array.from({ length: 4 }).map((_, ci) => <ShimmerBox key={ci} w={ci === 0 ? 65 : 50} h={12} r={4} />)}
                </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px' }}>
                <ShimmerBox w={80} h={12} r={4} />
                <div style={S.flex(6)}><ShimmerBox w={40} h={24} r={4} /><ShimmerBox w={40} h={24} r={4} /></div>
            </div>
        </div>
    </div>
);

// ─── Views ─────────────────────────────────────────────────

const DesktopOwnerView = () => {
    useCurrencySymbol(); // re-render when active currency changes (outlet switch)
    const state = useOwnerDashboard({ isDesktop: true });
    const data = useSalesData(state);

    const exportDateRangeLabel = exportPeriodFor(state.activeDate, state.dates)?.label ?? state.activeDate;

    // Fetches the WHOLE dataset for the active filters (date range / store / sort / view
    // tab) rather than just the page on screen — same "probe then re-fetch at full size"
    // pattern the other reports' table exports use (e.g. EOD's Funds/Summary tables).
    // `opts.locationId` is set when this runs as part of an "All outlets" export.
    const buildSalesOverviewExport = useCallback(async (_format: string, opts?: { locationId?: string }) => {
        const loc = opts?.locationId ?? state.locationId;
        const isToday = state.dates.startDate === state.today && state.dates.endDate === state.today;
        const payload: any = { locationId: loc, page: 0, size: 1 };
        if (!isToday) { payload.startDate = state.dates.startDate; payload.endDate = state.dates.endDate; }
        if (state.sortBy) { payload.sortBy = state.sortBy; payload.sortAsc = state.sortDir === 'asc'; }
        if (state.isAllStores) payload.considerThirdPartyValues = true;

        const probe = parseApiResponse(await getSalesOverviewByDays(payload));
        const total = probe?.totalElements || 1;
        const full = parseApiResponse(await getSalesOverviewByDays({ ...payload, size: total }));
        const list = getTableData(full);

        const buildRow = (row: any, isTotals = false) => state.columns.map((col: any) => {
            if (col.key === 'date') return isTotals ? 'TOTALS' : (row.date ?? '—');
            if (col.isCurrency) return formatMoneyForExport(num(row[col.key]));
            if (col.isInt) return num(row[col.key]);
            return row[col.key] ?? '—';
        });

        const rows = list.map((row: any) => buildRow(row));
        if (full?.totals) rows.push(buildRow(full.totals, true));

        return {
            headers: state.columns.map((col: any) => col.label),
            rows,
            totalRow: !!full?.totals,
        };
    }, [state.locationId, state.activeDate, state.today, state.dates.startDate, state.dates.endDate, state.sortBy, state.sortDir, state.isAllStores, state.columns]);

    if (state.locationsLoading) return <DesktopLayout><DesktopOwnerShimmer /></DesktopLayout>;
    if (!state.isSuperUser) return <Navigate to="/reports/snapshot" replace />;

    return (
        <DesktopLayout>
            <div style={{ padding: 24, maxWidth: 1280 }}>
                <Filters {...state} styleOverride={{ marginBottom: 20, gap: 16, alignItems: 'flex-end' }} />
                <KPIRow totals={data.totals} loading={data.tableLoading} channel={state.channel} />
                <SalesTable
                    columns={state.columns} data={data.tableData} totals={data.totals} loading={data.tableLoading}
                    tableError={data.tableError} onRetry={data.retry}
                    sortBy={state.sortBy} sortDir={state.sortDir} onSort={state.handleSort}
                    currentPage={state.currentPage} totalPages={data.tableResp?.totalPages}
                    totalElements={data.tableResp?.totalElements}
                    rowsPerPage={state.rowsPerPage} onPageChange={state.setCurrentPage}
                    onRowsPerPageChange={state.setRowsPerPage}
                    buildExport={buildSalesOverviewExport} exportDateRangeLabel={exportDateRangeLabel}
                    channel={state.channel}
                />
            </div>
        </DesktopLayout>
    );
};

// ─── Multi-Outlet Landing helpers ──────────────────────────

const getLogoFromOutlet = (outlet: any): string => {
    if (outlet.logoUrl && typeof outlet.logoUrl === 'string') return outlet.logoUrl;
    const media = outlet.media;
    if (Array.isArray(media)) {
        const img = media.find((m: any) => m.type === 'LOGO' || m.mediaType === 'LOGO' || m.fileType?.includes('image'));
        if (img?.url || img?.mediaUrl) return img?.url ?? img?.mediaUrl;
    }
    return '';
};

const getOutletDisplayName = (outlet: any): string => {
    const name = outlet.locationName ?? outlet.name ?? '';
    if (name.includes(',')) return name.split(',').slice(1).join(',').trim() || name;
    return name;
};

const getBrandFromOutlet = (outlet: any): string => {
    const name = (outlet?.locationName ?? outlet?.name ?? '').trim();
    if (!name) return '';
    if (!name.includes(',')) return name;
    return name.split(',')[0].trim();
};

const extractKpi = (res: any) => {
    if (!res) return null;
    const unwrap = (r: any) => r?.metaDataInfo?.data ?? r?.content ?? r?.data ?? r?.result ?? r?.response ?? r;
    const d = unwrap(res);
    const obj = Array.isArray(d) ? d[0] : d;
    if (!obj || typeof obj !== 'object') return null;
    const g = (o: any, ...keys: string[]) => {
        for (const k of keys) {
            const v = o[k];
            if (v !== undefined && v !== null && v !== '' && !Number.isNaN(Number(v))) return Number(v);
        }
        return null;
    };
    return {
        netSales: g(obj, 'totalNetSales', 'netSales', 'totalSales', 'net_sales'),
        orders: g(obj, 'totalTransactions', 'totalOrders', 'orders', 'orderCount', 'totalOrderCount'),
        netSalesPct: g(obj, 'netSalesChangePercent'),
        ordersPct: g(obj, 'transactionsChangePercent'),
    };
};

const extractClickhouseKpi = (details: any, comparison: any) => {
    if (!details) return null;
    const netSales = details.netSales !== undefined ? Number(details.netSales) : null;
    const orders = details.orderCount !== undefined ? Number(details.orderCount) : null;
    const netSalesPct = comparison?.netSales !== undefined ? Number(comparison.netSales) : null;
    const ordersPct = comparison?.orderCount !== undefined ? Number(comparison.orderCount) : null;
    return { netSales, orders, netSalesPct, ordersPct };
};

/**
 * GLOBAL-DP-4 (ZAR-1663): outlet cards no longer carry their own comparison text — the
 * basis is stated once in the page-level date control. The resolved comparison period is
 * kept as a hover tooltip on the change percentage.
 */
const getMobileComparisonLabel = (activeDate: string) => comparisonTooltip(activeDate || 'Today');

// ─── Delta badge ────────────────────────────────────────────
const DeltaBadge = ({ pct }: { pct: number | null }) => {
    if (pct === null) return <span style={{ fontSize: 9, color: '#9CA3B8', fontWeight: 500 }}>N/A</span>;
    const isPos = pct > 0;
    const isZero = pct === 0;
    const color = isZero ? '#9CA3B8' : isPos ? '#16A34A' : '#DC2626';
    return (
        <span style={{ fontSize: 9, fontWeight: 600, color, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
            {!isZero && (isPos ? '↑' : '↓')} {Math.abs(pct).toFixed(1)}%
        </span>
    );
};

// ─── Outlet card skeleton ────────────────────────────────────
const OutletCardSkeleton = () => (
    <div style={{ background: '#FFFFFF', borderRadius: 14, border: '1px solid #E2E5EC', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ShimmerBox w={20} h={20} r={5} />
            <ShimmerBox w="65%" h={12} r={4} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[0, 1].map(i => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <ShimmerBox w="45%" h={8} r={3} />
                    <ShimmerBox w="60%" h={18} r={4} />
                    <ShimmerBox w="35%" h={8} r={3} />
                </div>
            ))}
        </div>
    </div>
);

// ─── Outlet card ─────────────────────────────────────────────
const OutletCard = ({
    outlet,
    kpi,
    onTap,
    comparisonLabel,
}: {
    outlet: any;
    kpi: any;
    onTap: (o: any) => void;
    comparisonLabel: string;
}) => {
    const logoUrl = getLogoFromOutlet(outlet);
    const displayName = getOutletDisplayName(outlet);
    const initial = displayName.charAt(0).toUpperCase() || '?';
    const hasData = kpi !== null;
    return (
        <div
            onClick={() => onTap(outlet)}
            style={{ background: '#FFFFFF', borderRadius: 14, border: '1px solid rgba(229,35,51,0.18)', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: '14px 16px', cursor: 'pointer', position: 'relative', transition: 'transform 0.12s ease' }}
            onMouseDown={e => { (e.currentTarget as HTMLDivElement).style.transform = 'scale(0.985)'; }}
            onMouseUp={e => { (e.currentTarget as HTMLDivElement).style.transform = 'scale(1)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.transform = 'scale(1)'; }}
        >
            <svg width="10" height="10" viewBox="0 0 10 10" style={{ position: 'absolute', top: 16, right: 14 }}>
                <path d="M3.5 2L7 5L3.5 8" fill="none" stroke="#E52333" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: hasData ? 14 : 0 }}>
                {logoUrl
                    ? <img src={logoUrl} alt="" style={{ width: 20, height: 20, borderRadius: 5, objectFit: 'cover', flexShrink: 0 }} />
                    : <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, background: 'linear-gradient(135deg, #E52333, #FB5463)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 9, fontWeight: 700 }}>{initial}</div>
                }
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1A1D2E', flex: 1, paddingRight: 20 }}>{displayName}</span>
            </div>
            {hasData ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <span style={{ fontSize: 8, fontWeight: 500, color: '#9CA3B8', textTransform: 'uppercase' as const, letterSpacing: 0.3 }}>Net Sales</span>
                        <span style={{ fontSize: 18, fontWeight: 700, color: '#1A1D2E' }}>{kpi.netSales !== null ? `${getCurrencySymbol()}${Number(kpi.netSales).toLocaleString('en-US')}` : '—'}</span>
                        <div title={comparisonLabel} style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                            <DeltaBadge pct={kpi.netSalesPct} />
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <span style={{ fontSize: 8, fontWeight: 500, color: '#9CA3B8', textTransform: 'uppercase' as const, letterSpacing: 0.3 }}>Orders</span>
                        <span style={{ fontSize: 18, fontWeight: 700, color: '#1A1D2E' }}>{kpi.orders !== null ? Number(kpi.orders).toLocaleString('en-US') : '—'}</span>
                        <DeltaBadge pct={kpi.ordersPct} />
                    </div>
                </div>
            ) : (
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 0' }}>
                    <svg width="14" height="14" viewBox="0 0 14 14"><circle cx="7" cy="7" r="6" fill="none" stroke="#D97706" strokeWidth="1.3" /><path d="M7 4.5V7.5M7 9V9.5" stroke="#D97706" strokeWidth="1.3" strokeLinecap="round" /></svg>
                    <span style={{ fontSize: 11, color: '#D97706', fontWeight: 500 }}>Data unavailable</span>
                </div>
            )}
        </div>
    );
};

// ─── Mobile Multi-Outlet Landing ─────────────────────────────
const MobileMultiOutletLanding = () => {
    useCurrencySymbol(); // re-render when active currency changes (outlet switch)
    const ownerView = useOwnerView();
    const { auth } = useAuth();
    const setTopBarProps = useDesktopTopBar();
    const navigate = useNavigate();

    const { isSuperUser, locationsLoading, brands, selectedBrand, changeBrand, filteredStores, openStoreAsBranch, allLocations } = ownerView;

    const token = useMemo(() => auth?.accessToken ?? auth?.access_token ?? null, [auth]);
    const today = useMemo(() => todayStr(), []);
    const yesterday = useMemo(() => yesterdayStr(), []);

    const [activeDate, setActiveDate] = useState('Today');
    useEffect(() => {
        setActiveDate('Today');
    }, []);
    const handleDateChange = useCallback((val: string) => setActiveDate(val), []);

    useEffect(() => {
        setTopBarProps({ title: 'My Outlets', showBack: false, hideOutlet: true, dateFilters: { value: activeDate, onChange: handleDateChange } });
    }, [setTopBarProps, activeDate, handleDateChange]);

    const dates = useMemo(() => getDateRange(activeDate, today, yesterday), [activeDate, today, yesterday]);

    const outlets = useMemo(() => filteredStores.filter((s: any) => !s.isAll), [filteredStores]);
    const outletBrands = useMemo(() => {
        const all = allLocations.filter((s: any) => !s.isAll);
        const totalCount = all.length;
        const individualBrands = brands
            .filter((b: any) => b.value !== '')
            .map((b: any) => {
                const brandValue = String(b.value ?? '').toLowerCase();
                const count = all.filter((loc: any) => {
                    const name = String(loc.locationName ?? loc.name ?? '').toLowerCase();
                    return name.startsWith(brandValue);
                }).length;
                return { ...b, outletCount: count };
            })
            .filter((b: any) => b.outletCount > 0);
        return [{ label: 'All', value: '', outletCount: totalCount }, ...individualBrands];
    }, [brands, allLocations]);
    const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);
    const brandDropdownRef = useRef<HTMLDivElement | null>(null);
    const comparisonLabel = useMemo(() => getMobileComparisonLabel(activeDate), [activeDate]);

    useEffect(() => {
        if (!outletBrands.length) return;
        if (!selectedBrand) {
            changeBrand(outletBrands[0]);
        }
    }, [outletBrands, selectedBrand, changeBrand]);

    useEffect(() => {
        if (!brandDropdownOpen) return;
        const onDown = (e: MouseEvent) => {
            if (brandDropdownRef.current && !brandDropdownRef.current.contains(e.target as Node)) {
                setBrandDropdownOpen(false);
            }
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setBrandDropdownOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [brandDropdownOpen]);

    const [outletKPIs, setOutletKPIs] = useState<Record<string, any>>({});
    const [kpiLoading, setKpiLoading] = useState(true);
    const fetchIdRef = useRef(0);

    // Pull-to-refresh: this screen has no Redux slice / refetch() to call, so
    // a nonce is bumped on refresh and added to the fetch effect's deps below
    // (same pattern as a `drillView === 'x'`-gated effect elsewhere in
    // Reports — none of the effect's other deps change on a manual refresh).
    const [refreshNonce, setRefreshNonce] = useState(0);
    const handlePtrRefresh = useCallback(() => setRefreshNonce((n) => n + 1), []);
    const { scrollRef: ptrScrollRef, pullDistance, isRefreshing: ptrRefreshing } = usePullToRefresh({ onRefresh: handlePtrRefresh });

    useEffect(() => {
        const itemsToFetch = outlets;
        if (!itemsToFetch.length || !dates.startDate || !token) {
            setOutletKPIs({});
            setKpiLoading(false);
            return;
        }
        const fetchId = ++fetchIdRef.current;
        setKpiLoading(true);
        setOutletKPIs({});
        const isToday = dates.startDate === today && dates.endDate === today;
        const dateParams = isToday ? {} : { startDate: dates.startDate, endDate: dates.endDate };
        Promise.allSettled(
            itemsToFetch.map((outlet: any) => {
                const locationId = String(outlet.locationId ?? outlet.id);
                return Promise.all([
                    getSnapshotDetails({ locationId, token, ...dateParams }).catch(() => null),
                    getSnapshotComparison({ locationId, token, ...dateParams }).catch(() => null),
                ]);
            })
        ).then(results => {
            if (fetchId !== fetchIdRef.current) return;
            const kpis: Record<string, any> = {};
            itemsToFetch.forEach((outlet: any, i: number) => {
                if (results[i].status === 'fulfilled') {
                    const [details, comparison] = results[i].value as [any, any];
                    kpis[String(outlet.locationId ?? outlet.id)] = extractClickhouseKpi(details, comparison);
                } else {
                    kpis[String(outlet.locationId ?? outlet.id)] = null;
                }
            });
            setOutletKPIs(kpis);
            setKpiLoading(false);
        });
    }, [outlets, dates.startDate, dates.endDate, token, today, refreshNonce]);

    const hasKpisForCurrentOutlets = useMemo(() => {
        if (!outlets.length) return true;
        return outlets.every((outlet: any) => {
            const outletId = String(outlet.locationId ?? outlet.id);
            return Object.prototype.hasOwnProperty.call(outletKPIs, outletId);
        });
    }, [outlets, outletKPIs]);
    const showKpiSkeleton = kpiLoading || !hasKpisForCurrentOutlets;

    const handleCardTap = useCallback((outlet: any) => {
        openStoreAsBranch(outlet);
        navigate('/reports/snapshot');
    }, [openStoreAsBranch, navigate]);

    if (!isSuperUser) return <Navigate to="/reports/snapshot" replace />;

    if (locationsLoading) {
        return (
            <Phone>
                <TopBar variant="mobile" />
                <div style={{ flex: 1, overflowY: 'auto', background: '#F5F6FA', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[0, 1, 2].map(i => <OutletCardSkeleton key={i} />)}
                </div>
                <BottomNav />
            </Phone>
        );
    }

    if (allLocations.length === 1) {
        return <Navigate to="/reports/snapshot" replace />;
    }

    const sortedOutlets = [...outlets].sort((a: any, b: any) => {
        const aKpi = outletKPIs[String(a.locationId ?? a.id)];
        const bKpi = outletKPIs[String(b.locationId ?? b.id)];
        return (bKpi?.netSales ?? -1) - (aKpi?.netSales ?? -1);
    });

    return (
        <Phone>
            <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={ptrRefreshing} />
            <TopBar variant="mobile" />
            <div ref={ptrScrollRef} style={{ flex: 1, overflowY: 'auto', background: '#F5F6FA' }}>
                {outletBrands.length > 1 && (
                    <div style={{ padding: '10px 16px 0' }}>
                        <div ref={brandDropdownRef} style={{ position: 'relative' }}>
                            <button
                                type="button"
                                onClick={() => setBrandDropdownOpen((p) => !p)}
                                style={{
                                    width: '100%',
                                    minHeight: 42,
                                    borderRadius: 10,
                                    border: `1px solid ${brandDropdownOpen ? '#E52333' : '#E0E4ED'}`,
                                    background: '#FFFFFF',
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    boxShadow: brandDropdownOpen ? '0 0 0 3px rgba(229,35,51,0.08)' : '0 1px 2px rgba(0,0,0,0.04)',
                                }}
                            >
                                {(() => {
                                    const activeBrand = outletBrands.find((b: any) =>
                                        String(b.value ?? '').toLowerCase() === String(selectedBrand?.value ?? '').toLowerCase()
                                    );
                                    const shownBrand = activeBrand ?? outletBrands[0];
                                    return (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                            <div style={{
                                                width: 20, height: 20, borderRadius: 6,
                                                background: 'linear-gradient(135deg, #E52333, #FB5463)',
                                                color: '#fff', fontSize: 10, fontWeight: 700,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                            }}>
                                                {String(shownBrand?.label ?? '?').charAt(0).toUpperCase()}
                                            </div>
                                            <div style={{ minWidth: 0, textAlign: 'left' }}>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1A1D2E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {shownBrand?.label ?? 'Brand'}
                                                </div>
                                                <div style={{ fontSize: 10, color: '#9CA3B8' }}>
                                                    {shownBrand?.outletCount ?? 0} outlet{(shownBrand?.outletCount ?? 0) !== 1 ? 's' : ''}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}
                                <span style={{ display: 'flex', alignItems: 'center', color: '#9CA3B8' }}>
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transform: `rotate(${brandDropdownOpen ? 180 : 0}deg)`, transition: 'transform 0.2s' }}>
                                        <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </span>
                            </button>
                            {brandDropdownOpen && (
                                <div style={{
                                    position: 'absolute',
                                    top: 'calc(100% + 4px)',
                                    left: 0,
                                    right: 0,
                                    zIndex: 100,
                                    background: '#FFFFFF',
                                    border: '1px solid #E0E4ED',
                                    borderRadius: 10,
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.04)',
                                    overflow: 'hidden',
                                }}>
                                    {outletBrands.map((b: any, idx: number) => {
                                        const isActive = String(selectedBrand?.value ?? '').toLowerCase() === String(b.value ?? '').toLowerCase();
                                        return (
                                            <div
                                                key={String(b.value)}
                                                onClick={() => {
                                                    changeBrand(b);
                                                    setBrandDropdownOpen(false);
                                                }}
                                                style={{
                                                    padding: '10px 12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    cursor: 'pointer',
                                                    background: isActive ? '#FEF2F2' : 'transparent',
                                                    borderBottom: idx === outletBrands.length - 1 ? 'none' : '1px solid #F1F3F7',
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                                    <div style={{
                                                        width: 20, height: 20, borderRadius: 6,
                                                        background: isActive ? 'linear-gradient(135deg, #E52333, #FB5463)' : '#F3F4F8',
                                                        color: isActive ? '#fff' : '#5A6178', fontSize: 10, fontWeight: 700,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                                    }}>
                                                        {String(b.label).charAt(0).toUpperCase()}
                                                    </div>
                                                    <span style={{ fontSize: 13, fontWeight: isActive ? 600 : 500, color: isActive ? '#E52333' : '#1A1D2E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {b.label}
                                                    </span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <span style={{ fontSize: 11, color: '#9CA3B8' }}>
                                                        {b.outletCount} outlet{b.outletCount !== 1 ? 's' : ''}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                )}
                <div style={{ padding: '10px 16px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#5A6178' }}>
                        {showKpiSkeleton ? '' : `${sortedOutlets.length} Outlet${sortedOutlets.length !== 1 ? 's' : ''}`}
                    </span>
                    {!showKpiSkeleton && sortedOutlets.length > 0 && <span style={{ fontSize: 9, color: '#9CA3B8' }}>Sorted by net sales</span>}
                </div>
                <div style={{ padding: '0 16px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {showKpiSkeleton
                        ? [0, 1, 2].map(i => <OutletCardSkeleton key={i} />)
                        : sortedOutlets.map((outlet: any) => {
                            const outletId = String(outlet.locationId ?? outlet.id);
                            return (
                                <OutletCard
                                    key={outletId}
                                    outlet={outlet}
                                    kpi={outletKPIs[outletId] ?? null}
                                    onTap={handleCardTap}
                                    comparisonLabel={comparisonLabel}
                                />
                            );
                        })
                    }
                </div>
            </div>
            <BottomNav />
        </Phone>
    );
};

// ─── Export ─────────────────────────────────────────────────
export default function OwnerViewLandingPage() {
    const isDesktop = useIsDesktop();
    return isDesktop ? <DesktopOwnerView /> : <MobileMultiOutletLanding />;
}


